import React, { useState, useEffect } from 'react';
import axios from 'axios';
import axiosInstance from '../../../api';
import CasesAddModal from './modals/Cases/CasesAddModal';
import Pagination from '../Tools/Pagination';

const CasesTab = () => {
  // State management
  const [cases, setCases] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    case_name: '',
    case_type: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    limit: 10
  });
  const [totalRecords, setTotalRecords] = useState(0);

  /**
   * Shows alert message to user
   * @param {string} message - The message to display
   * @param {boolean} isSuccess - Whether it's a success message (default true)
   */
  const showAlert = (message, isSuccess = true) => {
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);
  };

  /**
   * Fetches cases data from API
   */
  const fetchCases = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/case', { 
        params: {
          search: filters.search,
          page: filters.page,
          limit: filters.limit
        } 
      });
      setCases(response.data?.data || response.data || []);
      setTotalRecords(response.data?.total || response.data?.length || 0);
    } catch (error) {
      console.error('Error fetching cases:', error);
      showAlert('Failed to fetch cases', false);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchCases();
  }, [filters]);

  /**
   * Handles input changes for form fields
   * @param {Object} e - The event object
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Handles filter input changes
   * @param {Object} e - The event object
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value, page: 1 })); // Reset to page 1 when filters change
  };

  /**
   * Handles page change
   * @param {number} newPage - The new page number
   */
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  /**
   * Resets all filters to default values
   */
  const clearFilters = () => {
    setFilters({
      search: '',
      page: 1,
      limit: 10
    });
  };

  /**
   * Opens modal for adding new case
   */
  const openAddModal = () => {
    setIsEditMode(false);
    setFormData({
      case_name: '',
      case_type: ''
    });
    setShowModal(true);
  };

  /**
   * Opens modal for editing existing case
   * @param {Object} caseItem - The case object to edit
   */
  const handleEdit = (caseItem) => {
    setIsEditMode(true);
    setCurrentCaseId(caseItem.id);
    setFormData({
      id: caseItem.id,
      case_name: caseItem.case_name,
      case_type: caseItem.case_type
    });
    setShowModal(true);
  };

  /**
   * Saves case data (create or update)
   */
  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      if (isEditMode) {
        // Update existing case
        await axiosInstance.put(`/case/${currentCaseId}`, formData);
        showAlert('Case updated successfully!');
      } else {
        // Create new case
        await axiosInstance.post('/case', formData);
        showAlert('Case added successfully!');
      }

      setShowModal(false);
      fetchCases();
    } catch (error) {
      console.error('Save failed:', error);
      showAlert(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} case`, false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Deletes a case
   * @param {number} id - The ID of the case to delete
   */
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this case?')) {
      try {
        setIsLoading(true);
        await axiosInstance.delete(`/case/${id}`);
        showAlert('Case deleted successfully!');
        fetchCases();
      } catch (error) {
        console.error('Delete failed:', error);
        showAlert('Failed to delete case', false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    
    <div className="tab-pane active" id="cases" role="tabpanel">
      {/* Filter Section */}
       <div className="filter-search mt-3">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-12 p-2">
              <input
                type="text"
                className="form-control"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search"
              />
            </div>
            </form>
            <button 
              className="btn m-2 d-flex align-items-center justify-content-center btn-outline-primary waves-effect waves-light"
              onClick={openAddModal}
            >
              <i className="ti ti-circle-plus fs-4 me-2"></i>
              Add Case
            </button>
          
        </div>
      </div>

      {/* Data Table Section */}
      {isLoading ? (
        <div className="text-center py-4">Loading cases...</div>
      ) : (
        <div className="py-3 px-0">
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap customize-table mb-0 align-middle">
              <thead className="text-dark fs-4">
                <tr>
                  <th><h6 className="fw-semibold mb-0">ID</h6></th>
                  <th><h6 className="fw-semibold mb-0">Case Name</h6></th>
                  <th><h6 className="fw-semibold mb-0">Case Type</h6></th>
                  <th><h6 className="fw-semibold mb-0">Actions</h6></th>
                </tr>
              </thead>
              <tbody>
                {cases.length > 0 ? (
                  cases.map((caseItem) => (
                    <tr key={caseItem.id}>
                      <td><p className="mb-0 fw-normal">{caseItem.id}</p></td>
                      <td><p className="mb-0 fw-normal">{caseItem.case_name}</p></td>
                      <td><p className="mb-0 fw-normal">{caseItem.case_type}</p></td>
                      <td>
                        <button 
                          className="h4 me-3 border-0 bg-transparent" 
                          onClick={() => handleEdit(caseItem)}
                        >
                          <i className="ti ti-edit text-warning"></i>
                        </button>
                        <button 
                          className="h4 border-0 bg-transparent" 
                          onClick={() => handleDelete(caseItem.id)}
                        >
                          <i className="ti ti-trash text-danger"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4">No cases found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Section */}
          {totalRecords > 0 && (
            <Pagination
              total={totalRecords}
              limit={filters.limit}
              page={filters.page}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Cases Add/Edit Modal */}
      <CasesAddModal
        show={showModal}
        onClose={() => setShowModal(false)}
        formData={formData}
        onChange={handleInputChange}
        onSave={handleSave}
        isEditMode={isEditMode}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CasesTab;